import {
    ContentCopyRounded,
    SaveAltRounded,
    VisibilityRounded,
} from "@mui/icons-material";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import copy from "copy-to-clipboard";
import React, { useMemo, useState } from "react";

/**
 * Above this size, inline rendering is gated behind an explicit click. Laying out
 * very large JSON synchronously can freeze the tab for seconds, and for big run
 * outputs the useful actions are Copy/Download, not scrolling the text.
 */
const INLINE_RENDER_LIMIT = 200_000; // characters (~200 KB)

function formatSize(chars: number): string {
    if (chars < 1024) return `${chars} B`;
    if (chars < 1024 * 1024) return `${(chars / 1024).toFixed(1)} KB`;
    return `${(chars / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Renders a run's raw JSON with size-aware performance guards. Copy and Download
 * are always available and cheap; the (potentially huge) text is only rendered
 * inline for small payloads, or on demand for large ones.
 */
export function RawJsonPanel({
    value,
    downloadFileName,
}: {
    value: unknown;
    downloadFileName: string;
}) {
    const jsonString = useMemo(() => {
        let parsed = value;
        if (typeof value === "string") {
            try {
                parsed = JSON.parse(value);
            } catch {
                // Not JSON — fall back to the raw string.
                return value;
            }
        }
        return JSON.stringify(parsed, null, 2) ?? String(value);
    }, [value]);

    const isLarge = jsonString.length > INLINE_RENDER_LIMIT;
    const [revealed, setRevealed] = useState(!isLarge);

    const handleDownload = () => {
        const fileName = downloadFileName.endsWith(".json")
            ? downloadFileName
            : `${downloadFileName}.json`;
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Chip
                    size="small"
                    variant="outlined"
                    label={formatSize(jsonString.length)}
                />
                <Box sx={{ flex: 1 }} />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyRounded />}
                    onClick={() => copy(jsonString)}
                >
                    Copy
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveAltRounded />}
                    onClick={handleDownload}
                >
                    Download
                </Button>
            </Box>

            {revealed ? (
                <Box
                    component="pre"
                    sx={{
                        backgroundColor: "gray.100",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.78em",
                        m: 0,
                        maxHeight: "60vh",
                        overflow: "auto",
                        p: 1.5,
                        whiteSpace: "pre",
                        wordBreak: "normal",
                    }}
                >
                    <code>{jsonString}</code>
                </Box>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 3,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        This JSON is large ({formatSize(jsonString.length)}).
                        Rendering it inline may briefly cause slowdowns — copy
                        or download it instead, or render it anyway.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<VisibilityRounded />}
                        onClick={() => setRevealed(true)}
                    >
                        Render inline anyway
                    </Button>
                </Paper>
            )}
        </Box>
    );
}

export default RawJsonPanel;
